/**
 * Admin — Data Subject Rights (DSR) queue.
 * Clinical admins review, decide and record outcomes on patient DSR requests
 * (access, correction, restriction, deletion, portability, account closure).
 * Also shows the read-only retention schedule so decisions are informed.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Pause, Clock, FileText, ShieldCheck } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";

const STATUS_META = {
  open:           { label: "Open",          icon: Clock,         cls: "text-amber-400" },
  under_review:   { label: "Under review",  icon: FileText,      cls: "text-blue-400" },
  approved:       { label: "Approved",      icon: CheckCircle2,  cls: "text-emerald-400" },
  denied:         { label: "Denied",        icon: XCircle,       cls: "text-red-400" },
  held_legal:     { label: "Held (legal)",  icon: Pause,         cls: "text-purple-400" },
  completed:      { label: "Completed",     icon: CheckCircle2,  cls: "text-emerald-500" },
};

export default function AdminDsr() {
  const [requests, setRequests] = useState([]);
  const [retention, setRetention] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState({});
  const [statusFilter, setStatusFilter] = useState("");
  const [openReq, setOpenReq] = useState(null);
  const [resolveStatus, setResolveStatus] = useState("approved");
  const [resolveNotes, setResolveNotes] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const [r, ret] = await Promise.all([
        api.get(statusFilter ? `/dsr/requests?status=${statusFilter}` : "/dsr/requests"),
        api.get("/retention-rules"),
      ]);
      setRequests(r.data);
      setRetention(ret.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [statusFilter]);

  const resolve = async () => {
    if (resolveNotes.trim().length < 3) { toast.error("Decision notes required (min 3 chars)."); return; }
    setResolving({ [openReq.id]: true });
    try {
      await api.patch(`/dsr/requests/${openReq.id}`, { status: resolveStatus, decision_notes: resolveNotes });
      toast.success("Decision recorded.");
      setOpenReq(null); setResolveNotes(""); setResolveStatus("approved");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Resolve failed"); }
    finally { setResolving({}); }
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl">
        <div className="fl-label text-[#f47a20]">Compliance</div>
        <h1 className="fl-heading text-3xl md:text-4xl mt-2">Data-subject requests</h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
          Review + record decisions on patient requests for access, correction, restriction or deletion. Deletion of clinical records is subject to the retention schedule below and must be justified in your decision notes.
        </p>

        <div className="mt-6 flex items-center gap-3">
          <label className="fl-label">Status</label>
          <select className="fl-input max-w-[14rem]" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="dsr-filter-status">
            <option value="">All</option>
            {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button onClick={refresh} className="fl-btn-ghost text-sm ml-auto" data-testid="dsr-refresh">
            {loading ? <Loader2 size={12} className="animate-spin"/> : "Refresh"}
          </button>
        </div>

        <div className="mt-4 space-y-2" data-testid="dsr-list">
          {requests.length === 0 && !loading && (
            <div className="fl-card p-8 text-center text-sm text-zinc-500">No requests match this filter.</div>
          )}
          {requests.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.open;
            const Icon = meta.icon;
            return (
              <button
                key={r.id}
                onClick={() => { setOpenReq(r); setResolveStatus(r.status === "open" ? "approved" : r.status); setResolveNotes(r.decision_notes || ""); }}
                className="fl-card p-4 w-full text-left hover:border-[#f47a20]/40 transition-colors flex items-center gap-4"
                data-testid={`dsr-row-${r.id}`}
              >
                <Icon className={meta.cls} size={18} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{r.request_type.replace("_", " ")}</span>
                    <span className={`text-[10px] uppercase tracking-widest ${meta.cls}`}>{meta.label}</span>
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {r.requester_email} · {new Date(r.created_at).toLocaleString()} · {r.reason.slice(0, 90)}{r.reason.length > 90 ? "…" : ""}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Retention schedule */}
        <section className="mt-10">
          <div className="fl-label text-[#f47a20] mb-2 flex items-center gap-2"><ShieldCheck size={12}/> Retention schedule</div>
          <div className="fl-card overflow-hidden" data-testid="dsr-retention-table">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-zinc-500 border-b" style={{ borderColor: "var(--fl-border)" }}>
                <tr>
                  <th className="text-left px-4 py-2">Category</th>
                  <th className="text-left px-4 py-2">Retention</th>
                  <th className="text-left px-4 py-2">Auto-delete</th>
                  <th className="text-left px-4 py-2">Legal basis</th>
                </tr>
              </thead>
              <tbody>
                {retention.map((r) => (
                  <tr key={r.category} className="border-b" style={{ borderColor: "var(--fl-border)" }} data-testid={`dsr-ret-${r.category}`}>
                    <td className="px-4 py-3">
                      <div className="text-zinc-200 font-medium">{r.label}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">{r.notes}</div>
                    </td>
                    <td className="px-4 py-3 text-zinc-300 whitespace-nowrap">{r.retention_years} yr</td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] uppercase tracking-widest ${r.auto_delete ? "text-emerald-400" : "text-amber-400"}`}>
                        {r.auto_delete ? "Yes" : "Manual review"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-zinc-400 text-xs">{r.legal_basis}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* Resolve modal */}
      {openReq && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-6 z-[100]" data-testid="dsr-resolve-modal">
          <div className="fl-card p-6 max-w-lg w-full">
            <div className="fl-label text-[#f47a20]">{openReq.request_type.replace("_", " ")} · {openReq.id.slice(0, 8)}</div>
            <div className="mt-2 text-xs text-zinc-500">
              from <b className="text-zinc-300">{openReq.requester_email}</b> · {new Date(openReq.created_at).toLocaleString()}
            </div>
            <div className="mt-4 fl-label">Patient reason</div>
            <div className="text-sm text-zinc-300 whitespace-pre-wrap p-3 rounded" style={{ background: "var(--fl-bg-2)", border: "1px solid var(--fl-border)" }}>{openReq.reason}</div>
            <div className="mt-4 fl-label">Decision</div>
            <select className="fl-input" value={resolveStatus} onChange={(e) => setResolveStatus(e.target.value)} data-testid="dsr-resolve-status">
              {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <div className="mt-3 fl-label">Decision notes (required)</div>
            <textarea className="fl-input" rows={4} value={resolveNotes} onChange={(e) => setResolveNotes(e.target.value)} data-testid="dsr-resolve-notes"/>
            <div className="mt-5 flex justify-end gap-2">
              <button className="fl-btn-ghost text-sm" onClick={() => setOpenReq(null)} data-testid="dsr-resolve-cancel">Cancel</button>
              <button className="fl-btn text-sm" onClick={resolve} disabled={resolving[openReq.id]} data-testid="dsr-resolve-save">
                {resolving[openReq.id] ? <Loader2 size={12} className="animate-spin"/> : "Save decision"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
